import { CreatePolicyDialog } from '@communities/content-management/components/CreatePolicyDialog';
import { PolicyDialogMode } from '@communities/content-management/domain';
import type { Policy, WorkflowType } from '@communities/content-management/domain';
import { useCommunityPolicies, useDeleteCommunityPolicy } from '@communities/content-management/hooks';
import { DataList, type DeleteItemContext, type TableColumn, type ActionConfig } from '@core/components';
import { SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Button, Badge, BadgeVariant, IconButton, Info16, IconButtonSize, IconSize, Section, CopyGames, ButtonType } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

interface PoliciesTabProps {
  communityId: string;
  isEntityAdmin: boolean;
  workflowType: WorkflowType.CLASSIFICATION | WorkflowType.MODERATION;
  sectionTitle?: string;
}

function PoliciesTabComponent({ communityId, isEntityAdmin, workflowType, sectionTitle = 'Policies' }: PoliciesTabProps) {
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | undefined>(undefined);
  const [dialogMode, setDialogMode] = useState<PolicyDialogMode>(PolicyDialogMode.Create);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { policies, loading, errors } = useCommunityPolicies(communityId, workflowType);
  const { deletePolicy, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunityPolicy(communityId);

  const columns: TableColumn<Policy>[] = [
    {
      key: 'name',
      header: 'Name',
      render: policy => policy.name,
      width: '2fr',
    },
    {
      key: 'description',
      header: 'Description',
      render: policy => policy.description || '-',
      width: '3fr',
    },
    {
      key: 'status',
      header: 'Status',
      render: policy => <Badge text={policy.isEnabled ? 'Enabled' : 'Disabled'} variant={policy.isEnabled ? BadgeVariant.Success : BadgeVariant.Error} />,
      width: '1fr',
    },
    {
      key: 'labels',
      header: 'Labels',
      render: policy => (
        <span>
          {policy.labels.length} {policy.labels.length === 1 ? 'label' : 'labels'}
        </span>
      ),
      width: '1fr',
    },
  ];

  const handleOpenDialog = useCallback((policy?: Policy, mode: PolicyDialogMode = PolicyDialogMode.Create) => {
    setSelectedPolicy(policy || undefined);
    setDialogMode(mode);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedPolicy(undefined);
    setDialogMode(PolicyDialogMode.Create);
  }, []);

  // Handle policy duplication
  const handleDuplicatePolicy = useCallback((policy: Policy) => {
    setSelectedPolicy(policy);
    setDialogMode(PolicyDialogMode.Duplicate);
    setIsDialogOpen(true);
  }, []);

  // Create duplicate action config
  const duplicateActionConfig: ActionConfig<Policy> | undefined = isEntityAdmin
    ? {
        icon: CopyGames,
        label: 'Duplicate policy',
        onClick: handleDuplicatePolicy,
        buttonType: ButtonType.Secondary,
        tooltip: policy => `Create a copy of "${policy.name}" policy`,
      }
    : undefined;

  // Create the delete item context
  const deleteItemContext: DeleteItemContext<Policy> | undefined = isEntityAdmin
    ? {
        handler: async (item: Policy) => await deletePolicy(communityId, item.id),
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader
          description="Configure policies for grouping content by labels and severity thresholds."
          endComponent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconButton
                aria-label="Learn more about policies"
                buttonSize={IconButtonSize.Medium}
                icon={Info16}
                iconSize={IconSize.XSmall}
                onClick={() => {
                  /* TODO: Add more info dialog */
                }}
              />
              {isEntityAdmin && <Button onClick={() => handleOpenDialog()}>ADD POLICY</Button>}
            </div>
          }
          title={`${sectionTitle} (${policies.length})`}
        />

        {/* Policies List */}
        <DataList
          additionalAction={duplicateActionConfig}
          columns={columns}
          data={policies}
          dataTypeName="policy"
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? policy => handleOpenDialog(policy, PolicyDialogMode.Edit) : undefined}
        />
      </Section>

      {/* Create/Edit Policy Dialog - Wizard Mode */}
      <CreatePolicyDialog
        communityId={communityId}
        isOpen={isDialogOpen}
        mode={dialogMode}
        policy={selectedPolicy}
        workflowType={workflowType}
        onClose={handleCloseDialog}
      />
    </>
  );
}

export const PoliciesTab = withAppInsightsTracking(PoliciesTabComponent, 'PoliciesTab');
